import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  ProductGridSkeleton,
  ProductSearchPanel,
  ProductSearchResultCard,
} from './product-search-panel'
import { useProducts } from '@/api/hooks'
import { formatMoney } from '@/lib/utils'
import {
  productsSearchFromState,
  type ProductSearchState,
  type ProductsSearch,
  type ShopifySearchProduct,
} from './search-state'
import { formatProductSearchCount as formatCount, getImageUrl } from './search-filters'

type ProductsPageProps = {
  search: ProductsSearch
  onSearchChange: (search: ProductsSearch) => void
}

export function ProductsPage({ search, onSearchChange }: ProductsPageProps) {
  const page = search.page ?? 1

  const setPage = (nextPage: number | ((currentPage: number) => number)) => {
    const resolvedPage = typeof nextPage === 'function' ? nextPage(page) : nextPage
    const nextSearch = { ...search }
    if (resolvedPage > 1) {
      nextSearch.page = resolvedPage
    } else {
      delete nextSearch.page
    }
    onSearchChange(nextSearch)
  }

  const syncProductSearch = (state: ProductSearchState) =>
    onSearchChange(productsSearchFromState(page, state))

  const { data, isLoading } = useProducts({
    page,
    limit: 20,
  })

  const renderPublicProduct = (product: ShopifySearchProduct) => (
    <ProductSearchResultCard product={product} />
  )

  const defaultResults = isLoading ? (
    <ProductGridSkeleton />
  ) : data?.items?.length ? (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.items.map((product) => {
          const imgUrl = getImageUrl(product.images)
          return (
            <article
              key={product.uuid}
              className="group relative rounded-lg border bg-card hover:shadow-md transition-shadow overflow-hidden flex flex-col"
            >
              <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={product.model}
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <div className="text-muted-foreground/30 text-4xl font-bold">
                    {product.model?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              <div className="p-3 flex flex-col gap-1.5 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-sm group-hover:text-primary leading-tight">
                    <Link
                      to="/products/$productId"
                      params={{ productId: product.uuid }}
                      className="after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {product.model}
                    </Link>
                  </h3>
                  <div className="flex gap-1 shrink-0">
                    {product.shopify_product_id && (
                      <Badge variant="default" className="text-[10px] px-1.5 py-0">
                        Shopify
                      </Badge>
                    )}
                    {product.discontinued_at && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                        Disc.
                      </Badge>
                    )}
                  </div>
                </div>
                {product.specifications && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {product.specifications}
                  </p>
                )}
                <div className="mt-auto pt-2 flex flex-col gap-1 text-xs">
                  <div className="flex items-center justify-between">
                    <Link
                      to="/manufacturers/$manuId"
                      params={{ manuId: product.manufacturer?.uuid ?? '' }}
                      className="relative z-10 text-primary hover:underline truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {product.manufacturer_name}
                    </Link>
                    {product.brand?.name && (
                      <span className="text-muted-foreground truncate ml-2">
                        {product.brand.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between font-medium">
                    <span>{formatMoney(product.current_price?.store_price)}</span>
                    <span className="text-muted-foreground">
                      Net {formatMoney(product.current_price?.net_price)}
                    </span>
                  </div>
                </div>
              </div>
            </article>
          )
        })}
      </div>

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {formatCount(data?.page ?? 1)} of {formatCount(data?.pages ?? 1)} (
          {formatCount(data?.total)} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  ) : (
    <p className="text-center text-muted-foreground py-12">No products found</p>
  )

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <ProductSearchPanel
        page={page}
        onPageChange={setPage}
        initialQuery={search.q}
        initialPerPage={search.per_page}
        initialSort={search.sort}
        initialPriceMin={search.price_min == null ? undefined : String(search.price_min)}
        initialPriceMax={search.price_max == null ? undefined : String(search.price_max)}
        onSearchStateChange={syncProductSearch}
        renderProduct={renderPublicProduct}
        defaultResults={defaultResults}
      />
    </div>
  )
}
